<!-- eslint-disable @typescript-eslint/no-magic-numbers -->
<template>
    <swiper
        :slides-per-view="1"
        :space-between="-50"
        :autoplay="false"
        :breakpoints="{
            768: {
                slidesPerView: 1,
            },
            1440: {
                slidesPerView: 1,
            },
            1600: {
                slidesPerView: 1,
            },
        }"
        :loop="loop"
        :pagination="paginationOptions"
        :modules="[Pagination, Autoplay]"
        @swiper="onSwiper"
        @slideChange="onSlideChange">
        <swiper-slide v-for="(slide, index) in slides" :key="`slide-${index}`">
            <div class="swiper-slide__content">
                <div class="slide-body">
                    <header class="title">{{ slide.Title }}</header>
                    <div class="subtitle" v-if="slide.Subtitle">
                        {{ slide.Subtitle }}
                    </div>

                    <a
                        v-if="slide.Link.Url && slide.Link.Text"
                        :href="slide.Link.Url"
                        :target="slide.Link.Target"
                        class="link btn-default"
                        >{{ slide.Link.Text }}</a
                    >
                </div>
                <div class="slide-overlay"></div>
                <picture>
                    <source
                        media="(min-width: 1024px)"
                        :srcset="`${slide.Image.ImageUrl}?height=560&format=webp&quality=80`"
                        type="image/webp" />
                    <source
                        media="(max-width: 768px)"
                        :srcset="`${
                            slide.MobileImage?.ImageUrl ?? slide.Image.ImageUrl
                        }?width=750&format=webp&quality=80`"
                        type="image/webp" />
                    <img
                        loading="lazy"
                        :src="slide.Image.ImageUrl"
                        :alt="slide.Image.AlternativeText" />
                </picture>
            </div>
        </swiper-slide>

        <div class="pagination"></div>
    </swiper>
</template>

<script lang="ts">
import type { Ref } from 'vue';
import { ISlide } from './interface/hero-carousel.model';
import { defineComponent, PropType, ref } from 'vue';
import { Pagination, Autoplay } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { PaginationOptions, AutoplayOptions, Swiper as SwiperT } from 'swiper/types';

export default defineComponent({
    components: {
        Swiper,
        SwiperSlide,
    },
    props: {
        loop: {
            type: Boolean,
            default: true,
        },
        slides: {
            type: Array as PropType<Array<ISlide>>, //https://forum.vuejs.org/t/specifying-array-type-of-prop-in-vue-3-0-with-typescript/103169
            required: true,
        },
    },
    setup(props) {
        const activeSlideIndex: Ref<number> = ref(0);
        const nextSlideIndex: Ref<number> = ref(0);
        const prevSlideIndex: Ref<number> = ref(0);
        const activeSlide: Ref<number> = ref(0);

        const autoPlayOptions: AutoplayOptions = {
            delay: 3000,
            pauseOnMouseEnter: true,
            disableOnInteraction: false,
        };

        const paginationOptions: PaginationOptions = {
            clickable: true,
            el: '.pagination',
        };

        const onSwiper = (swiper: SwiperT) => {
            activeSlideIndex.value = swiper.realIndex;
            nextSlideIndex.value = activeSlideIndex.value++;
            prevSlideIndex.value = activeSlideIndex.value--;
        };

        const onSlideChange = (e: any) => {
            if (e.slides[e.realIndex] !== activeSlide.value) {
                activeSlideIndex.value = getIndex(e.realIndex);
                nextSlideIndex.value = getIndex(e.realIndex + 1);
                prevSlideIndex.value = getIndex(e.realIndex - 1);
            }
            activeSlide.value = e.slides[e.realIndex];
        };

        const getIndex = (index: number) => {
            if (index < 0) {
                return props.slides.length - 1;
            }
            if (index >= props.slides.length) {
                return 0;
            }
            return index;
        };
        const getItem = (index: number) => {
            let realIndex = index;
            if (index < 0 || index >= props.slides.length) {
                realIndex = getIndex(index);
            }
            return props.slides[realIndex];
        };

        return {
            autoPlayOptions,
            paginationOptions,
            activeSlideIndex,
            nextSlideIndex,
            prevSlideIndex,
            onSwiper,
            onSlideChange,
            getItem,
            getIndex,
            Autoplay,
            Pagination,
        };
    },
});
</script>
